import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Clock, Calendar, User, Tag, BookOpen } from 'lucide-react';
import { BlogCardSkeleton } from './loading/SkeletonLoader';
import { OptimizedSpotlightGroup } from './OptimizedLandingPage';
import usePublicPosts from '../hooks/usePublicPosts';
import { sortPostsByDate, formatDate, calculateReadingTime, extractExcerpt } from '../utils/blogUtils';
import { scrollToTop } from '../utils/scrollUtils';
import { useTransition } from '../hooks/useTransition';

// Enhanced Blog Card Component for Recent Posts
const EnhancedBlogCard = ({ post }) => {
  const { navigateWithTransition, preloadRoute } = useTransition();
  
  if (!post) return null;

  // Parse tags if they're stored as JSON string
  const tags = typeof post.tags === 'string' ? JSON.parse(post.tags) : (post.tags || []);
  
  // Use created_at from database or publishedAt for compatibility
  const publishDate = post.created_at || post.publishedAt;
  
  // Generate excerpt if not provided
  const excerpt = post.excerpt || extractExcerpt(post.content, 180);
  
  // Calculate reading time
  const readingTime = calculateReadingTime(post.content);

  // Handle card click with smooth transition
  const handleCardClick = (e) => {
    // Prevent navigation if clicking on interactive elements
    if (e.target.tagName === 'A' || e.target.closest('a')) {
      return;
    }
    
    navigateWithTransition(`/blog/${post.slug}`);
  };

  // Handle hover for preloading
  const handleMouseEnter = () => {
    preloadRoute(`/blog/${post.slug}`);
  };

  return (
    <article
      className="enhanced-blog-card spotlight-card"
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      style={{ cursor: 'pointer' }}
    >
      <div className="spotlight-content">
        <div className="enhanced-blog-card-content">
          {/* Header with meta info */}
          <div className="enhanced-blog-card-header">
            <div className="enhanced-blog-card-meta">
              <div className="meta-item">
                <Calendar size={14} />
                <span>{formatDate(publishDate)}</span>
              </div>
              <div className="meta-item">
                <Clock size={14} />
                <span>{readingTime}</span>
              </div>
              {post.author && (
                <div className="meta-item">
                  <User size={14} />
                  <span>{post.author}</span>
                </div>
              )}
            </div>
          </div>

          {/* Title */}
          <h3 className="enhanced-blog-card-title">
            {post.title}
          </h3>

          {/* Excerpt */}
          <p className="enhanced-blog-card-excerpt">
            {excerpt}
          </p>

          {/* Tags */}
          {tags.length > 0 && (
            <div className="enhanced-blog-card-tags">
              <Tag size={14} className="tags-icon" />
              <div className="tags-list">
                {tags.slice(0, 3).map((tag, index) => (
                  <span key={index} className="enhanced-blog-tag">
                    {tag}
                  </span>
                ))}
                {tags.length > 3 && (
                  <span className="enhanced-blog-tag more-tags">
                    +{tags.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Read more indicator */}
          <div className="enhanced-blog-card-footer">
            <div className="read-more-indicator">
              <BookOpen size={16} />
              <span>Read Article</span>
              <ArrowRight size={14} className="arrow-icon" />
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

const RecentPosts = () => {
  const { posts, loading, error, getPublishedPosts } = usePublicPosts();
  const { navigateWithTransition } = useTransition();

  // Get the most recent published posts
  const recentPosts = React.useMemo(() => {
    const publishedPosts = getPublishedPosts();
    const sortedPosts = sortPostsByDate(publishedPosts);
    return sortedPosts.slice(0, 2);
  }, [posts, getPublishedPosts]);

  const handleButtonClick = (e) => {
    // Prevent navigation if clicking on interactive elements
    if (e.target.tagName === 'A' || e.target.closest('a')) {
      return;
    }
    
    navigateWithTransition(`/blog`);
  };

  // Loading state
  if (loading) {
    return (
      <section className="py-24 px-4 section-bg-light-glow">
        <div className="max-w-8xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                Recent Posts
              </span>
            </h2>
            <p className="text-xl text-slate-300">
              Discover our latest insights and technical deep-dives
            </p>
          </div>

          <div className="flex justify-center mb-12">
            <div className="w-full max-w-md">
              <BlogCardSkeleton />
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="py-24 px-4 section-bg-light-glow">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                Recent Posts
              </span>
            </h2>
            <p className="text-xl text-slate-300">
              Discover our latest insights and technical deep-dives
            </p>
          </div>

          <div className="text-center">
            <div className="bg-slate-700/50 rounded-lg p-8">
              <h3 className="text-xl font-semibold text-slate-300 mb-2">
                Unable to Load Posts
              </h3>
              <p className="text-slate-400">
                {error}
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Empty state
  if (recentPosts.length === 0) {
    return (
      <section className="py-24 px-4 section-bg-light-glow">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                Recent Posts
              </span>
            </h2>
            <p className="text-xl text-slate-300">
              Discover our latest insights and technical deep-dives
            </p>
          </div>

          <div className="text-center">
            <div className="bg-slate-700/50 rounded-lg p-8">
              <h3 className="text-xl font-semibold text-slate-300 mb-2">
                No Posts Available
              </h3>
              <p className="text-slate-400">
                Check back soon for new content!
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Main content with posts
  return (
    <section className="py-24 px-4 section-bg-light-glow">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              Latest Posts
            </span>
          </h2>
          <p className="text-xl text-slate-300">
            Discover our latest insights and technical deep-dives
          </p>
        </div>

        {/* Latest Blog Posts */}
        <OptimizedSpotlightGroup>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {recentPosts.map(post => (
              <EnhancedBlogCard key={post.id} post={post} />
            ))}
          </div>
        </OptimizedSpotlightGroup>

        {/* View All Posts Button */}
        <div className="text-center mt-12">
          <button
            onClick={handleButtonClick}
            className="inline-flex items-center gap-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl hover:shadow-purple-500/25"
          >
            View All Posts
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default RecentPosts;